<template>
  <div class="payment-information-form form-container">
    <div class="payment-information-form__inner">
      <h3>Payment Information</h3>
      <div :class="creditGroupClass">
        <label>Credit Card*</label>
        <div class="cc-num">
          <div class="cc-num__icon" ref="icon"></div>
          <input
            name="credit_card_number"
            pattern="[0-9 ]{13,23}"
            required=true
            type="tel"
            maxlength=23
            x-autocompletetype="cc-number"
            aria-label="Credit Card Number"
            ref="num"
            @input="checkCard"
            v-model="credit_card_number"
          />
          <InlineSvg src="images/icons/icon-lock.svg"></InlineSvg>
          <span class="error-msg cnum" v-if="errors.credit_card_number">
            {{errors.credit_card_number}}
          </span>
        </div>
        <div class="form-group">
          <div class="field-group expiry-group">
            <label>Exp Date*</label>
            <input
              type="text"
              name="expiry"
              required=true
              placeholder="MM / YY"
              maxlength=5
              aria-label="Expiration"
              ref="expiry"
              @input="maskexpiry"
              v-model="expiry"
            />
            <span class="expiry-format">MM / YY</span>
            <span class="error-msg" v-if="errors.expiry">
              {{errors.expiry}}
            </span>
          </div>
          <div class="field-group cvv-group">
            <label>CVV*</label>
            <input
              type="text"
              name="credit_card_cid"
              required=true
              placeholder="CVV"
              maxlength=4
              inputmode="numeric"
              pattern="[0-9]*"
              aria-label="CVV"
              ref="cvv"
              @input="maskcvv"
              v-model="credit_card_cid"
            />
            <span class="error-msg" v-if="errors.credit_card_cid">
              {{errors.credit_card_cid}}
            </span>
          </div>
        </div>
      </div>
      <div class="field-group">
        <label>Have a promo code?</label>
        <div class="promo-field" :class="{ applied: promoApplied, invalid: promo.invalid }">
          <div class="promo-input">
            <input
              type="text"
              name="coupon_code"
              v-model="coupon_code"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
            />
            <a ref="promoBtn" href="#" @click.prevent="submitPromo">
              {{promoSubmitText}}
            </a>
          </div>
          <div class="checkmark-circle">
            <InlineSvg src="images/icons/checkmark-white.svg"></InlineSvg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InlineSvg from 'vue-inline-svg';
import { mapFields } from 'vuex-map-fields';
import { mapGetters } from 'vuex';
import axios from "axios";

export default {
  props: ['errors'],
  data() {
    return {
      promo: {
        invalid: false,
        submitting: false
      }
    }
  },
  components: {
    InlineSvg
  },
  mounted() {
    this.checkCard()
    this.maskexpiry()
    this.cvvValidity()
  },
  updated() {
    this.maskexpiry()
    this.cvvValidity()
  },
  computed: {
    ...mapFields([
      'form.coupon_code',
      'form.credit_card_number',
      'form.credit_card_cid',
      'form.expiry'
    ]),
    ...mapGetters([
      'promoApplied'
    ]),
    promoSubmitText() {
      let text = this.promo.invalid ? 'Invalid Promo' : 'Submit'
      text = this.promo.submitting ? 'Submitting...' : text
      return this.promoApplied ? 'Remove' : text
    },
    creditGroupClass() {
      let classes = ['field-group', 'field-group__card']
      if (this.errors.credit_card_number) classes.push('error-cnum')
      if (this.errors.credit_card_cid) classes.push('error-cvv')
      if (this.errors.expiry) classes.push('error-expiry')
      return classes.join(' ')
    }
  },
  methods: {
    checkCard() {
      let number = this.$refs.num.value.split(" ").join("");
      let card_types = [
        {
          name: "American Express",
          abbr: "amex",
          regex: /^(?:3[47][0-9]{13})$/
        },
        {
          name: "Visa",
          abbr: "visa",
          regex: /^(?:4[0-9]{12}(?:[0-9]{3})?)$/
        },
        {
          name: "Mastercard",
          abbr: "mastercard",
          regex: /^(?:5[1-5][0-9]{14})$/
        },
        {
          name: "Discover",
          abbr: "discover",
          regex: /^(?:6(?:011|5[0-9][0-9])[0-9]{12})$/
        },
        {
          name: "Diners Club",
          abbr: "diners_club",
          regex: /^(?:3(?:0[0-5]|[68][0-9])[0-9]{11})$/
        },
        {
          name: "JCB",
          abbr: "jcb",
          regex: /^(?:(?:2131|1800|35\d{3})\d{11})$/
        }
      ];

      // remove letters
      let formatted = number.replace(/[a-zA-Z]/g, '')
      // add spacing every 4 digits
      formatted = (formatted.match(/.{1,4}/g) || []).join(' ')
      this.$store.commit('updateCnum', formatted)
      this.creditCardValidity()

      card_types.forEach(c => this.$refs.icon.classList.remove(c.abbr));
      if (!this.valid_credit_card(number)) return;
      let card_type = card_types.find(card_type => {
        return number.match(card_type.regex);
      });
      if (card_type) this.$refs.icon.classList.add(card_type.abbr);
      this.validate(this.$refs.num)
    },
    creditCardValidity() {
      let number = this.$refs.num.value.split(" ").join("");
      let msg = ''
      if (!this.valid_credit_card(number)) {
        msg = 'Invalid card number'
      }
      this.$refs.num.setCustomValidity(msg)
    },
    expiryValidity() {
      let expiry = this.$refs.expiry.value
      let msg = ''
      if (expiry == '') {
        msg = 'Missing exp'
      } else if (!expiry.match(/\d{2}\/\d{2}/)) {
        msg = 'Invalid exp'
      }
      this.$refs.expiry.setCustomValidity(msg)
    },
    cvvValidity() {
      if (this.$refs.cvv == undefined) return
      let cvv = this.$refs.cvv.value
      let msg = ''
      if (cvv == '') {
        msg = 'Missing cvv'
      }
      this.$store.commit('updateCvv', cvv)
      this.$refs.cvv.setCustomValidity(msg)
    },
    valid_credit_card(value) {
      value = value.replace(/\s/g, "");
      // Accept only digits, dashes or spaces
      if (/[^0-9-\s]+/.test(value)) return false;

      // The Luhn Algorithm.
      let nCheck = 0,
          bEven = false;
      value = value.replace(/\D/g, "");

      for (var n = value.length - 1; n >= 0; n--) {
        let cDigit = value.charAt(n);
        let nDigit = parseInt(cDigit, 10);

        if (bEven && (nDigit *= 2) > 9) nDigit -= 9;

        nCheck += nDigit;
        bEven = !bEven;
      }

      return nCheck % 10 == 0;
    },
    maskexpiry() {
      if (this.$refs.expiry == undefined) return
      const val = this.$refs.expiry.value;
      const formattted = val.replace(/.*?(\d).*(\d).*(\d).*(\d).*/g, "$1$2/$3$4");
      this.$store.commit('updateExpiry', formattted)
      this.expiryValidity()
      this.validate(this.$refs.expiry)
    },
    maskcvv() {
      if (this.$refs.cvv == undefined) return
      this.cvvValidity()
      this.validate(this.$refs.cvv)
    },
    submitPromo() {
      let request = this.$store.state.request
      if (this.promoApplied) {
        request.coupon_code = ''
        this.$store.commit('removePromo')
      } else {
        request.coupon_code = this.$store.state.form.coupon_code
      }
      this.$store.commit('updateQuoteRequest', request)

      // add authenticity_token for csrf protection
      request.authenticity_token = document.querySelector('[name="csrf-token"]').content

      this.promo.submitting = true
      axios
        .put('/checkout/quote', request)
        .then(({ data: response }) => {
          this.promo.submitting = false
          if (!this.promoApplied) {
            this.promo.invalid = true
            setTimeout(() => { this.promo.invalid = false }, 3000)
          }
          this.$store.commit('updateReservation', response)
        })
    },
    validate(field) {
      if (field.checkValidity()) {
        if (field.name == 'credit_card_number') {
          field.parentNode.parentNode.classList.remove('error-cnum')
        }
        if (field.name == 'expiry') {
          field.parentNode.parentNode.parentNode.classList.remove('error-expiry')
        }
        if (field.name == 'credit_card_cid') {
          field.parentNode.parentNode.parentNode.classList.remove('error-cvv')
        }
        field.parentNode.classList.remove('error')
        if (this.$parent.errors) {
          this.$parent.errors[field.name] = null
        }
      }
    }
  }
}
</script>

<style>
.payment-information-form {
  &__inner {
    padding: 50px 0;
    border-top: 2px solid #dcdcdc;
    border-bottom: 2px solid #dcdcdc;
  }
}

.form-group {
  display: flex;
  justify-content: space-between;
  .field-group {
    position: relative;
    width: 47%;
  }
}

.error-expiry {
  .expiry-group {
    input {
      border: 1px solid $error;
    }
  }
}

.error-cvv {
  .cvv-group {
    input {
      border: 1px solid $error;
    }
  }
}

.error-cnum {
  .cc-num {
    input {
      border: 1px solid $error;
    }
  }
}

.error-expiry,
.error-cvv,
.error-cnum {
  .error-msg {
    display: block;
  }
}

.expiry-format {
  position: absolute;
  display: none;
  bottom: -20px;
  left: 7px;
  color: $gray;
  font-size: 12px;
  background: $white;
  z-index: 1;
  width: 75px;
}

input[name="expiry"]:focus + .expiry-format {
  display: block;
}

input[name="expiry"]:focus + .expiry-format {
  display: block;
}

.cc-num,
.promo-field {
  display: flex;
  align-items: center;

  svg {
    flex-shrink: 0;
    margin: 0 20px;
  }
}

.field-group .cc-num {
  position: relative;

  input {
    padding: 0 0 0 60px;
  }

  &__icon {
    position: absolute;
    left: 20px;
    width: 30px;
    height: 18px;
    background-image: url(~icons/credit-card-icons/credit-card.svg);
    background-size: cover;

    &.amex {
      background-image: url(~icons/credit-card-icons/Amex.svg);
    }

    &.discover {
      background-image: url(~icons/credit-card-icons/Discover.svg);
    }

    &.mastercard {
      background-image: url(~icons/credit-card-icons/Mastercard.svg);
    }

    &.visa {
      background-image: url(~icons/credit-card-icons/Visa.svg);
    }
  }
}

.promo-field {
  &.applied {
    .checkmark-circle {
      background: #042f5b;
    }
  }
}

.promo-input {
  position: relative;
  width: 100%;

  a {
    position: absolute;
    right: 15px;
    top: 15px;
    font-size: 16px;
    line-height: 1.38;
    letter-spacing: 0.25px;
    text-align: right;
    color: #555555;
  }
}

.invalid {
  .promo-input {
    border: 1px solid $error;
  }
}

.checkmark-circle {
  height: 20px;
  width: 20px;
  border-radius: 100%;
  background: #ddd;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 20px;
  svg {
    height: 10px;
  }
}

@mixin breakpoint $tablet {
  .field-group {
    &__card {
      position: relative;

      .form-group {
        position: absolute;
        right: 50px;
        bottom: 0;
        width: 180px;

        label {
          display: none;
        }

        input {
          border: 0;
          padding: 8px;
          background: transparent;
        }

      }
    }
  }

  .error-expiry,
  .error-cvv,
  .error-cnum {
    .error-msg {
      display: block;
    }

    [name="credit_card_number"] {
      border: 1px solid $error;
    }

    .error-msg {
      left: 7px;
    }

    .error-msg.cnum {
      left: 0;
    }

    .expiry-group,
    .cvv-group {
      label {
        margin-left: 7px;
        display: block;
      }
    }
  }

  .field-group__card .form-group {
    .expiry-group,
    .cvv-group {
      label {
        margin-left: 7px;
        display: block;
      }
    }
  }

  .promo-field {
    width: 75%;
  }
}
</style>